import React from 'react';
import { motion } from 'framer-motion';

interface AmbientBackgroundProps {
  complianceStatus: 'compliant' | 'warning' | 'violation';
  intensity?: number; // 0-1, affects animation speed and opacity
}

const AmbientBackground: React.FC<AmbientBackgroundProps> = ({
  complianceStatus,
  intensity = 0.5,
}) => {
  // Color schemes based on compliance status
  const colorSchemes = {
    compliant: {
      primary: 'rgba(16, 185, 129, 0.05)',   // Green
      secondary: 'rgba(52, 211, 153, 0.03)',
      glow: 'rgba(16, 185, 129, 0.1)',
    },
    warning: {
      primary: 'rgba(245, 158, 11, 0.08)',   // Yellow/Orange
      secondary: 'rgba(251, 191, 36, 0.05)',
      glow: 'rgba(245, 158, 11, 0.15)',
    },
    violation: {
      primary: 'rgba(239, 68, 68, 0.1)',     // Red
      secondary: 'rgba(248, 113, 113, 0.06)',
      glow: 'rgba(239, 68, 68, 0.2)',
    },
  };

  const colors = colorSchemes[complianceStatus];
  const animationDuration = 20 - (intensity * 10); // Faster when intensity is higher

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden">
      {/* Gradient orbs */}
      <motion.div
        className="absolute -top-1/4 -left-1/4 w-1/2 h-1/2 rounded-full blur-3xl"
        style={{
          background: `radial-gradient(circle, ${colors.primary} 0%, transparent 70%)`,
        }}
        animate={{
          x: [0, 100, 0],
          y: [0, 50, 0],
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{
          duration: animationDuration,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />

      <motion.div
        className="absolute -bottom-1/4 -right-1/4 w-1/2 h-1/2 rounded-full blur-3xl"
        style={{
          background: `radial-gradient(circle, ${colors.secondary} 0%, transparent 70%)`,
        }}
        animate={{
          x: [0, -100, 0],
          y: [0, -50, 0],
          scale: [1, 1.1, 1],
          opacity: [0.2, 0.4, 0.2],
        }}
        transition={{
          duration: animationDuration * 1.5,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />

      <motion.div
        className="absolute top-1/3 left-1/3 w-1/3 h-1/3 rounded-full blur-3xl"
        style={{
          background: `radial-gradient(circle, ${colors.glow} 0%, transparent 70%)`,
        }}
        animate={{
          x: [-50, 50, -50],
          y: [-30, 30, -30],
          scale: [1, 1.3, 1],
          opacity: [0.1, 0.3, 0.1],
        }}
        transition={{
          duration: animationDuration * 1.2,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />

      {/* Pulsing effect for violations */}
      {complianceStatus === 'violation' && (
        <motion.div
          className="absolute inset-0"
          style={{
            background: `radial-gradient(circle at center, ${colors.glow} 0%, transparent 50%)`,
          }}
          animate={{
            opacity: [0, intensity * 0.3, 0],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      )}

      {/* Mesh gradient overlay for depth */}
      <div
        className="absolute inset-0 opacity-30"
        style={{
          background: `
            radial-gradient(at 20% 30%, ${colors.primary} 0px, transparent 50%),
            radial-gradient(at 80% 70%, ${colors.secondary} 0px, transparent 50%),
            radial-gradient(at 50% 50%, ${colors.glow} 0px, transparent 50%)
          `,
        }}
      />
    </div>
  );
};

export default AmbientBackground;
